/**
 * Fila de envio do POST /logs (#837 / audit item 2).
 * Antes cada evento era 1 POST e uma pagina com varios paineis/eventos estourava o
 * limite por IP (429) e perdia os erros do front. Agora: lote com debounce,
 * dedupe de Erro por mensagem+stack, teto por sessao e nada de reenvio em 429.
 */

export type LogItem = {
  Tipo: string;
  Objeto?: unknown;
  LoginId?: number;
  UserId?: string;
};

export type ResultadoEnvio = { status: number; retryAfterSeg?: number };
export type ResultadoLog = { ok: boolean; descartado?: "dedupe" | "teto" | "bloqueado" | "falha" };

export const MAX_ITENS_POR_LOTE = 20;

export type LogQueueOptions = {
  send: (itens: LogItem[]) => Promise<ResultadoEnvio>;
  debounceMs?: number;
  maxLote?: number;
  tetoSessao?: number;
  bloqueioPadraoMs?: number;
  agora?: () => number;
};

type Pendente = { item: LogItem; resolve: (r: ResultadoLog) => void };

function textoCurto(v: unknown, max: number): string {
  return typeof v === "string" ? v.slice(0, max) : "";
}

/** Chave de dedupe de Erro: mensagem + stack (sem rota, para nao repetir o mesmo bug em cada tela). */
export function chaveDedupeErro(item: LogItem): string | null {
  if (item.Tipo !== "Erro") return null;
  const objeto = (item.Objeto ?? {}) as Record<string, any>;
  const erro = (objeto.error ?? {}) as Record<string, any>;
  const mensagem = textoCurto(erro.Message ?? erro.message ?? erro.apiMessage, 500);
  const stack = textoCurto(erro.stack, 1000);
  const operacao = textoCurto(erro.operation, 200);
  if (!mensagem && !stack && !operacao) return null;
  return `${mensagem}|${stack}|${operacao}`;
}

export function createLogQueue(opts: LogQueueOptions) {
  const debounceMs = opts.debounceMs ?? 1500;
  const maxLote = Math.min(opts.maxLote ?? MAX_ITENS_POR_LOTE, MAX_ITENS_POR_LOTE);
  const tetoSessao = opts.tetoSessao ?? 300;
  const bloqueioPadraoMs = opts.bloqueioPadraoMs ?? 60_000;
  const agora = opts.agora ?? (() => Date.now());

  let fila: Pendente[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let aceitosNaSessao = 0;
  let bloqueadoAte = 0;
  const errosVistos = new Set<string>();

  function agendar() {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, debounceMs);
  }

  function enqueue(item: LogItem): Promise<ResultadoLog> {
    if (agora() < bloqueadoAte) return Promise.resolve({ ok: false, descartado: "bloqueado" });
    const chave = chaveDedupeErro(item);
    if (chave) {
      if (errosVistos.has(chave)) return Promise.resolve({ ok: false, descartado: "dedupe" });
      if (errosVistos.size < 500) errosVistos.add(chave);
    }
    // Erros sempre tem prioridade: o teto por sessao vale so para eventos de uso.
    if (item.Tipo !== "Erro") {
      if (aceitosNaSessao >= tetoSessao) return Promise.resolve({ ok: false, descartado: "teto" });
      aceitosNaSessao++;
    }
    return new Promise<ResultadoLog>((resolve) => {
      fila.push({ item, resolve });
      if (fila.length >= maxLote) {
        void flush();
      } else {
        agendar();
      }
    });
  }

  async function flush(): Promise<void> {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    while (fila.length > 0) {
      const lote = fila.splice(0, maxLote);
      if (agora() < bloqueadoAte) {
        lote.forEach((p) => p.resolve({ ok: false, descartado: "bloqueado" }));
        continue;
      }
      let resultado: ResultadoEnvio;
      try {
        resultado = await opts.send(lote.map((p) => p.item));
      } catch {
        resultado = { status: 0 };
      }
      if (resultado.status === 429) {
        // Nao reenvia: so para de mandar ate a janela passar.
        bloqueadoAte = agora() + (resultado.retryAfterSeg ? resultado.retryAfterSeg * 1000 : bloqueioPadraoMs);
        lote.forEach((p) => p.resolve({ ok: false, descartado: "bloqueado" }));
        fila.splice(0).forEach((p) => p.resolve({ ok: false, descartado: "bloqueado" }));
        return;
      }
      const ok = resultado.status >= 200 && resultado.status < 300;
      lote.forEach((p) => p.resolve(ok ? { ok: true } : { ok: false, descartado: "falha" }));
    }
  }

  return { enqueue, flush, pendentes: () => fila.length };
}